import { api } from "./client";

// Owner Recapitalization Loan (spec pack docs/owner-recap/approved, letters.json
// composer_notes 0, 14–16) — the Conditional Approval letter families and the
// per-deal letter options the composer's picker reads:
//   GET /collection/letter-options?dealId=
// The server guards every add (the family guard and one Seller letter per deal in
// CollectionPackageService.AddItemCoreAsync); this is the picker's view of it.

/** The letter lineages — mirrors ConditionalApprovalLetterFamily /
 *  CollectionBundleIds (pinned by conditionalApprovalLetters.test.ts, which reads
 *  the C# source). A template id names one version; match on `lineageId`. */
export const LETTER_LINEAGES = {
  acquisitionBorrower: "c1d2e3f4-0001-4abc-9def-0123456789a1",
  acquisitionSeller: "c1d2e3f4-0002-4abc-9def-0123456789a2",
  recapBorrower: "842a4a4f-7c82-40bf-863a-f6081ea2882c",
  recapSeller: "3731c0cf-b159-4f3d-afcd-213e30d80946",
  recapSellerOptionE: "c83c2bb8-f8f2-410b-a450-792d85f007bc",
  acquisitionSellerOptionE: "5a44c09f-7055-4c11-88d9-6ad94e1a58f7",
} as const;

const BORROWER_LINEAGES: readonly string[] = [
  LETTER_LINEAGES.acquisitionBorrower,
  LETTER_LINEAGES.recapBorrower,
];
const SELLER_LINEAGES: readonly string[] = [
  LETTER_LINEAGES.acquisitionSeller,
  LETTER_LINEAGES.recapSeller,
  LETTER_LINEAGES.recapSellerOptionE,
  LETTER_LINEAGES.acquisitionSellerOptionE,
];
const RECAP_LINEAGES: readonly string[] = [
  LETTER_LINEAGES.recapBorrower,
  LETTER_LINEAGES.recapSeller,
  LETTER_LINEAGES.recapSellerOptionE,
];

const norm = (id: string | null | undefined) => (id ?? "").toLowerCase();

export const isBorrowerLetter = (lineageId: string | null | undefined) =>
  BORROWER_LINEAGES.includes(norm(lineageId));
export const isSellerLetter = (lineageId: string | null | undefined) =>
  SELLER_LINEAGES.includes(norm(lineageId));
export const isConditionalApprovalLetter = (lineageId: string | null | undefined) =>
  isBorrowerLetter(lineageId) || isSellerLetter(lineageId);
export const isRecapLetter = (lineageId: string | null | undefined) =>
  RECAP_LINEAGES.includes(norm(lineageId));
export const isRecapBorrowerLetter = (lineageId: string | null | undefined) =>
  norm(lineageId) === LETTER_LINEAGES.recapBorrower;
/** composer_notes 9 — no Buyer stage on the two recap Seller letters. */
export const refusesBuyerStage = (lineageId: string | null | undefined) =>
  norm(lineageId) === LETTER_LINEAGES.recapSeller ||
  norm(lineageId) === LETTER_LINEAGES.recapSellerOptionE;

/** composer_notes 3, 5 — the values shared by the two letters of one send: an
 *  edit on one letter is copied to the other. */
export const LETTER_SYNC_KEYS = [
  "BaseFinancedAmount",
  "NoteHolderSignerName",
  "NoteHolderSignerTitle",
] as const;

export type DealTypeCode = "Acquisition" | "OwnerRecapitalization";

export interface ConditionalApprovalLetterOptionDto {
  lineageId: string;
  /** The lineage's current active version — the id to add. */
  templateId: string;
  templateName: string;
  documentTitle: string;
  party: "Borrower" | "Seller";
  isRecap: boolean;
  isOptionE: boolean;
  preselected: boolean;
}

export interface ActiveSellerLetterDto {
  itemId: string;
  packageId: string;
  lineageId: string;
  templateName: string;
}

export interface ConditionalApprovalLetterOptionsDto {
  dealId: string;
  dealType: DealTypeCode;
  optionERecorded: boolean;
  letters: ConditionalApprovalLetterOptionDto[];
  preselectedSellerLineageId: string | null;
  activeSellerLetter: ActiveSellerLetterDto | null;
}

export const conditionalApprovalLettersApi = {
  options: (dealId: string) =>
    api.get<ConditionalApprovalLetterOptionsDto>("/collection/letter-options", { dealId }),
};

/** A template the picker may offer for the deal: every non-letter template, and
 *  the letters the deal's options allow. Without a deal the recap letters are
 *  hidden (they need an Owner Recapitalization deal); while the options load,
 *  letters are hidden rather than offered wrongly. */
export function letterTemplateAllowed(
  lineageId: string | null | undefined,
  dealId: string | null,
  options: ConditionalApprovalLetterOptionsDto | undefined,
): boolean {
  if (!isConditionalApprovalLetter(lineageId)) return true;
  if (!dealId) return !isRecapLetter(lineageId);
  if (!options) return false;
  return options.letters.some((l) => norm(l.lineageId) === norm(lineageId));
}
